import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { Add, Trash, Warning2 } from 'iconsax-react';
import type { FC, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import type { CompanySummary } from '@core/types/api/CompanySummary/CompanySummary';
import type { DispatchCustomer } from '@core/types/api/DispatchCustomer/DispatchCustomer';
import type { DispatchCustomerMatchInfo } from '@core/types/api/DispatchCustomerMatchInfo/DispatchCustomerMatchInfo';
import { NewDispatchCustomerMatchInfo } from '@core/types/api/DispatchCustomerMatchInfo/DispatchCustomerMatchInfo';
import type { UnlinkedDispatchCustomer } from '@core/types/api/UnlinkedDispatchCustomer/UnlinkedDispatchCustomer';
import type { UnmergedDispatchCustomer } from '@core/types/api/UnmergedDispatchCustomer/UnmergedDispatchCustomer';
import { hasValue } from '@core/utils/equality';
import { YupNullableReference, YupSometimesNullableString } from '@core/utils/yupHelpers';
import { InputDropdown, SlabCard } from '@web/components';
import { ApiLookupInput } from '@web/components/LookupInput/ApiLookupInput';
import {
  ConstructListQueryParams,
  DEFAULT_LOOKUP_LENGTH,
  type LookupInputOption,
} from '@web/components/LookupInput/LookupInputSharedComponents';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { useToast } from '@web/providers/ToastProvider/useToast';
import { lookups } from '@web/utils/DomainHelpers';

import { CustomerInfo } from './CustomerInfo';
import { LinkRoundIcon } from './LinkRoundIcon';

type MergeDispatchCustomersFormikType = {
  dispatchCustomers: DispatchCustomerMatchInfo[];
  selectedDispatchCustomer: { id: string | null };
  selectedCompany: { id: string; option: LookupInputOption | null } | null;
  customerName: string | null;
};

const getInitialValues = (data: UnmergedDispatchCustomer): MergeDispatchCustomersFormikType => ({
  dispatchCustomers: data.matchInfo ?? [],
  selectedDispatchCustomer: { id: null },
  selectedCompany: data.id
    ? {
        id: data.id,
        option: {
          value: data.id,
          label: data.name,
        },
      }
    : null,
  customerName: data.id ? data.name : null,
});

const mergeDispatchCustomersSchema = Yup.object({
  selectedCompany: YupNullableReference('Select Company').required('Company must be selected'),
  dispatchCustomers: Yup.array()
    .min(1, 'At least one dispatch customer must be selected')
    .required('At least one dispatch customer must be selected'),
  customerName: YupSometimesNullableString('Customer name', true).required(
    'Customer name must be selected',
  ),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  data: UnmergedDispatchCustomer;
  allowChangeCompany?: boolean;
  linkedDispatchCustomers?: DispatchCustomer[];
  onSaveAsDraft?: () => void;
};

export const MergeCustomerModal: FC<Props> = ({
  isOpen,
  onClose,
  data,
  allowChangeCompany = false,
  onSubmit,
  linkedDispatchCustomers,
  onSaveAsDraft,
}) => {
  const theme = useTheme();
  const [showLookup, setShowLookup] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setShowLookup(false);
  }, [data]);

  const mergeMutation = useSlabMutation('PUT dispatch customers merge');

  const hasLinkedDispatchCustomers =
    hasValue(linkedDispatchCustomers) && linkedDispatchCustomers.length > 0;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        '& .MuiDialog-paper': {
          width: '900px',
          maxWidth: 'calc(100vw - 64px)',
        },
      }}
    >
      <Formik
        validationSchema={mergeDispatchCustomersSchema}
        initialValues={getInitialValues(data)}
        onSubmit={async (values) => {
          if (!values.selectedCompany) {
            return;
          }

          const dispatchCustomerIDs = values.dispatchCustomers.map((dc) => dc.dispatchCustomerId);

          if (dispatchCustomerIDs.length === 0) {
            return;
          }

          const finalCustomerName = values.customerName || undefined;

          mergeMutation.mutate(
            {
              args: {
                body: {
                  companyId: values.selectedCompany.id,
                  dispatchCustomerIds: dispatchCustomerIDs,
                  companyName: finalCustomerName,
                },
              },
            },
            {
              onSuccess() {
                toast.success({ message: 'Customer records merged and linked' });
                onClose();
                onSubmit?.();
              },
              onError() {
                toast.error({ message: 'Failed to merge customer records' });
              },
            },
          );
        }}
      >
        {(formikBag): ReactElement => {
          const hasSelectedDispatchCustomers = formikBag.values.dispatchCustomers.length > 0;

          const companyNameOptions = [
            ...(formikBag.values.selectedCompany?.option?.label
              ? [
                  {
                    value: formikBag.values.selectedCompany.option.label,
                    label: formikBag.values.selectedCompany.option.label,
                  },
                ]
              : []),
            ...formikBag.values.dispatchCustomers.map((dc) => ({
              value: dc.dispatchCustomerName,
              label: dc.dispatchCustomerName,
            })),
            ...(hasLinkedDispatchCustomers
              ? linkedDispatchCustomers.map((dc) => ({
                  value: dc.name,
                  label: dc.name,
                }))
              : []),
          ].filter((opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx);
          return (
            <>
              <DialogTitle variant='h4' sx={{ fontWeight: 500 }} id='alert-dialog-title'>
                Merge Customer Records
              </DialogTitle>
              <DialogContent>
                <Box gap={2} display='flex' flexDirection='column'>
                  <Alert
                    severity='info'
                    icon={<Warning2 variant='TwoTone' />}
                    sx={{ borderRadius: 2 }}
                  >
                    Merging creates ONE unified customer in Slabstack that links to multiple
                    dispatch systems. When exporting to dispatch, we&apos;ll use the original names
                    in each system.
                  </Alert>
                  {!allowChangeCompany && (
                    <SlabCard
                      sx={{
                        backgroundColor: theme.palette.SlabGray[50],
                        padding: 1.5,
                        my: 0,
                        gap: 2,
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      <Typography variant='h5/medium'>
                        Slabstack Customer:{' '}
                        <Typography variant='h6' component='span'>
                          {data.name}
                        </Typography>
                      </Typography>

                      {hasLinkedDispatchCustomers && (
                        <Box>
                          <Typography variant='h6'>Linked Dispatch Customers:</Typography>
                          <Grid container spacing={1}>
                            {linkedDispatchCustomers.map((dispatchCustomer) => (
                              <Grid item xs={12} sm={4} key={dispatchCustomer.id}>
                                <CustomerInfo
                                  customerInfo={{
                                    integrationType:
                                      dispatchCustomer.integrationSummary?.integrationType,
                                    dispatchCustomerName: dispatchCustomer.name,
                                    address: dispatchCustomer.address?.line1,
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      <LinkRoundIcon />
                    </SlabCard>
                  )}
                  <SlabCard
                    sx={{
                      backgroundColor: theme.palette.SlabGray[50],
                      padding: 1.5,
                      my: 0,
                      gap: 1,
                      position: 'relative',
                      overflow: 'visible',
                    }}
                  >
                    <Typography variant='h5/medium' mb={1}>
                      Dispatch Records (will be merged to unified customer)
                    </Typography>
                    <FieldArray
                      name='dispatchCustomers'
                      render={(arrayHelpers) => (
                        <>
                          <Grid
                            container
                            spacing={2}
                            sx={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
                          >
                            {formikBag.values.dispatchCustomers.map((record, index) => {
                              return (
                                <Grid item xs={12} sm={4} key={`s-${record.dispatchCustomerId}`}>
                                  <CustomerInfo
                                    onClose={() => {
                                      arrayHelpers.remove(index);
                                      if (
                                        record.dispatchCustomerName ===
                                        formikBag.values.customerName
                                      ) {
                                        // first option from companyNameOptions
                                        formikBag.setFieldValue(
                                          'customerName',
                                          companyNameOptions[0].value,
                                        );
                                      }
                                    }}
                                    customerInfo={{
                                      integrationType: record.integrationType,
                                      dispatchCustomerName: record.dispatchCustomerName,
                                      address: record.address,
                                      matchStrength: record.matchStrength,
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                          {showLookup && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <ApiLookupInput
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      backgroundColor: theme.palette.SlabGray[0],
                                    },
                                  }}
                                  formState={formikBag.values}
                                  name='selectedDispatchCustomer.id'
                                  label='Select Dispatch Customer'
                                  route={{
                                    barrel: 'GET unlinked dispatch customers',
                                    args: (inputText) =>
                                      ConstructListQueryParams<UnlinkedDispatchCustomer>(
                                        inputText,
                                        {
                                          page: 0,
                                          perPage: DEFAULT_LOOKUP_LENGTH,
                                        },
                                      ),
                                    transform: (dispatchCustomerList): LookupInputOption[] => {
                                      const selectedDispatchCustomerIds = new Set(
                                        formikBag.values.dispatchCustomers.map(
                                          (dc) => dc.dispatchCustomerId,
                                        ),
                                      );
                                      const filteredItems = dispatchCustomerList?.items.filter(
                                        (dc) => !selectedDispatchCustomerIds.has(dc.id),
                                      );
                                      return lookups({
                                        items: filteredItems,
                                        label: (dc) => dc.name,
                                        sublabels: (dc) => (dc.code ? [dc.code] : []),
                                        count: filteredItems?.length ?? 0,
                                      });
                                    },
                                  }}
                                  onMatchChange={(dispatchCustomerId, dispatchCustomerList) => {
                                    if (dispatchCustomerId && dispatchCustomerList) {
                                      const selectedCustomer = dispatchCustomerList.items.find(
                                        (dc) => dc.id === dispatchCustomerId,
                                      );
                                      if (selectedCustomer) {
                                        const newMatchInfo = NewDispatchCustomerMatchInfo({
                                          dispatchCustomerId: selectedCustomer.id,
                                          dispatchCustomerName: selectedCustomer.name,

                                          integrationType: selectedCustomer.integrationType,
                                        });
                                        arrayHelpers.push(newMatchInfo);
                                        formikBag.setFieldValue('selectedDispatchCustomer', {
                                          id: null,
                                        });
                                        setShowLookup(false);
                                      }
                                    }
                                  }}
                                />
                                {typeof formikBag.errors.dispatchCustomers === 'string' &&
                                  (formikBag.touched.dispatchCustomers ||
                                    formikBag.submitCount > 0) && (
                                    <Typography
                                      variant='body2'
                                      sx={{ color: theme.palette.error.main, mt: 1 }}
                                    >
                                      {formikBag.errors.dispatchCustomers}
                                    </Typography>
                                  )}
                              </Box>
                              <IconButton
                                onClick={() => {
                                  setShowLookup(false);
                                  formikBag.setFieldValue('selectedDispatchCustomer', { id: null });
                                }}
                                sx={{ mt: 1 }}
                              >
                                <Trash variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
                              </IconButton>
                            </Box>
                          )}
                          {!showLookup && hasSelectedDispatchCustomers && (
                            <Box sx={{ display: 'flex' }}>
                              <Button
                                variant='slab-transparent'
                                color='primary'
                                startIcon={<Add size={20} variant='TwoTone' />}
                                sx={{ padding: '0px' }}
                                onClick={() => setShowLookup(true)}
                              >
                                Add Another Record
                              </Button>
                            </Box>
                          )}
                          {!showLookup && !hasSelectedDispatchCustomers && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                padding: 2.5,
                                alignItems: 'center',
                              }}
                            >
                              <img
                                src='/add_customer_Illustration.png'
                                style={{
                                  width: '170px',
                                  height: 'auto',
                                }}
                                alt='add customer illustration'
                              />
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant='h6/medium'>No Dispatch records</Typography>
                                <Typography variant='body2'>
                                  Please select at least one dispatch record to merge
                                </Typography>
                                {typeof formikBag.errors.dispatchCustomers === 'string' &&
                                  (formikBag.touched.dispatchCustomers ||
                                    formikBag.submitCount > 0) && (
                                    <Typography
                                      variant='body2'
                                      sx={{ color: theme.palette.error.main, mt: 1 }}
                                    >
                                      {formikBag.errors.dispatchCustomers}
                                    </Typography>
                                  )}
                              </Box>

                              <Button
                                variant='slab-transparent'
                                color='primary'
                                startIcon={<Add size={20} variant='TwoTone' />}
                                sx={{
                                  padding: '0px',
                                  backgroundColor: theme.palette.SlabGray[0],
                                  border: `1px solid ${theme.palette.SlabGray.Stroke}`,
                                }}
                                onClick={() => setShowLookup(true)}
                              >
                                Add Another Record
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    />
                    <LinkRoundIcon />
                  </SlabCard>
                  {allowChangeCompany && (
                    <SlabCard
                      sx={{
                        backgroundColor: theme.palette.SlabGray[50],
                        padding: 1.5,
                        my: 0,
                        gap: 1,
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      <Typography variant='h5/medium'>Potential Slabstack Match</Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        You can keep Potential Slabstack Match or search for different customer
                      </Typography>

                      <ApiLookupInput
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: theme.palette.SlabGray[0],
                          },
                        }}
                        formState={formikBag.values}
                        name='selectedCompany'
                        label='Select Company'
                        onMatchChange={(companyId, companyData) => {
                          if (companyId) {
                            formikBag.setFieldValue(
                              'customerName',
                              companyData?.items?.find((c: CompanySummary) => c.id === companyId)
                                ?.name,
                            );
                          } else {
                            formikBag.setFieldValue('customerName', null);
                          }
                        }}
                        route={{
                          barrel: 'GET company summaries',
                          args: (inputText) =>
                            ConstructListQueryParams<CompanySummary>(inputText, {
                              page: 0,
                              perPage: DEFAULT_LOOKUP_LENGTH,
                            }),
                          transform: (companyList): LookupInputOption[] =>
                            lookups({
                              items: companyList?.items,
                              label: (c) => c.name,
                              count: companyList?.count ?? 0,
                            }),
                        }}
                      />

                      {hasSelectedDispatchCustomers && <LinkRoundIcon />}
                    </SlabCard>
                  )}
                  {hasSelectedDispatchCustomers && (
                    <SlabCard
                      sx={{
                        backgroundColor: theme.palette.SlabGray[50],
                        padding: 1.5,
                        my: 0,
                        gap: 1,
                      }}
                    >
                      <Typography variant='h5/medium'>Unified Slabstack Customer Record</Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        This name will be used throughout Slabstack. Dispatch systems will keep
                        their original names.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <InputDropdown
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: theme.palette.SlabGray[0],
                            },
                          }}
                          name='customerName'
                          label='Customer Name*'
                          options={companyNameOptions}
                        />
                      </Box>
                    </SlabCard>
                  )}
                </Box>
              </DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  px: 3,
                  py: 2,
                }}
              >
                <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onClose}>
                  Cancel
                </Button>
                {onSaveAsDraft && !hasLinkedDispatchCustomers && (
                  <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onSaveAsDraft}>
                    Save as Draft
                  </Button>
                )}
                <Button
                  variant='slab-primary'
                  sx={{ flex: 1 }}
                  onClick={formikBag.submitForm}
                  disabled={mergeMutation.isPending}
                >
                  {mergeMutation.isPending ? 'Merging...' : 'Merge'}
                </Button>
              </Box>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};
